/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import client from "../apollo/client";
import Cookies from "universal-cookie";
import config from "../config/env";
import { fireEvent } from "../events/Events";
import { IS_DEV } from "../utils/constants";
import { jwtDecode } from "jwt-decode";

export type AuthFailure = { message: string; action?: string };
export type AuthResult<T> = { ok: true; value: T } | { ok: false; error: AuthFailure };

export type LoginUser = {
  id?: number;
  accountType?: string;
  firstName?: string;
  phone?: string;
  familyId?: number;
  authyStatus?: string;
  challengeToken?: string;
  token?: string;
  requires2FA?: boolean;
};

export type VerifiedUser = {
  id?: number;
  accountType?: string;
  firstName?: string;
  token?: string;
  twoFAtoken?: string;
};

export type RegistrationRequest = {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  password: string;
  accountType: string;
  timeZoneOffset: string;
  timeZone: string;
};

type TokenClaims = { userId?: number; principalId?: number };

const AUTH_COOKIE = "manateeAuthToken";
const TWO_FACTOR_COOKIE = "manatee2FA";
const COOKIE_LIFETIME_MS = 25920000000;
const JSON_HEADERS = { Accept: "application/json", "Content-Type": "application/json" };

const CONNECTION_FAILURES = {
  login: "Unable to log in. Please check your connection and try again.",
  submitPhone: "Error sending phone number. Please contact support@getmanatee.com",
  requestCode: "Error requesting a new code. Please contact support@getmanatee.com",
  verifyCode: "Error submitting verification code. Please contact support@getmanatee.com",
  register: "Unable to register. Please check your connection and try again.",
  registrationOptions: "Error calling isCareCoordinationEnabled",
  forgotPassword: "Unable to request a password reset. Please check your connection and try again.",
  passwordReset: "Unable to reset your password. Please check your connection and try again."
};

const GENERIC_FAILURE = "Something went wrong. Please try again later.";
const PHONE_FAILURE = "Error submitting phone number. Please contact support@getmanatee.com";
const INVALID_CODE =
  "Invalid code. Please try receiving a new code. Contact support@getmanatee.com if problem persists.";

const cookies = new Cookies();

function ok<T>(value: T): AuthResult<T> {
  return { ok: true, value };
}

function fail<T>(message: string, action?: string): AuthResult<T> {
  return { ok: false, error: action === undefined ? { message } : { message, action } };
}

function post(path: string, body: unknown, headers: Record<string, string> = {}): Promise<Response> {
  return fetch(`${config.env.REACT_APP_API}${path}`, {
    method: "POST",
    headers: { ...JSON_HEADERS, ...headers },
    body: JSON.stringify(body)
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function jsonOrNull(response: Response): Promise<any> {
  return response.json().catch(() => null);
}

function stringOr(value: unknown, fallback: string): string {
  return typeof value === "string" && value !== "" ? value : fallback;
}

const AuthService = {
  isAuthenticated: (): boolean => !!cookies.get(AUTH_COOKIE),
  getAuthToken: (): string | undefined => cookies.get(AUTH_COOKIE),
  decodeToken: (): TokenClaims | null => {
    const token = cookies.get(AUTH_COOKIE);
    if (!token) {
      return null;
    }
    try {
      return jwtDecode<TokenClaims>(token);
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  },
  hasUserId: (): boolean => AuthService.decodeToken()?.userId !== undefined,
  isAuthenticatedExternalProvider: (): boolean => AuthService.decodeToken()?.principalId !== undefined,
  role: (): string | false => sessionStorage.getItem("role") || false,

  login: async (username: string, password: string): Promise<AuthResult<LoginUser>> => {
    if (cookies.get(AUTH_COOKIE)) {
      logout();
    }
    fireEvent("loginBegin");
    const trimmed = username.trim();
    const twoFAtoken = cookies.get(TWO_FACTOR_COOKIE);
    let response: Response;
    try {
      response = await post(
        "/login",
        { application: "WEB", username: trimmed, password },
        { "x-2FA-token": twoFAtoken ? `${twoFAtoken}` : "" }
      );
    } catch {
      return fail(CONNECTION_FAILURES.login);
    }
    const body = await jsonOrNull(response);
    if (!response.ok) {
      return fail(
        stringOr(body?.error, "Error logging in"),
        typeof body?.action === "string" ? body.action : undefined
      );
    }
    if (!body) {
      return fail("Error logging in");
    }
    const user = body as LoginUser;
    setSessionValue("userId", user.id);
    setSessionValue("authyStatus", user.authyStatus);
    setSessionValue("role", user.accountType);
    setSessionValue("user", user.firstName);
    setSessionValue("phone", user.phone);
    setSessionValue("familyId", user.familyId);
    setSessionValue("email", trimmed);
    setSessionValue("challengeToken", user.challengeToken);
    if (user.token) {
      setAuthTokenCookie(user.token);
      fireEvent("loginComplete");
    }
    return ok(user);
  },

  submitPhone: async (account: {
    userId: string | null;
    email?: string | undefined;
    phone: string;
  }): Promise<AuthResult<void>> => {
    let response: Response;
    try {
      response = await post("/submitPhone", account);
    } catch {
      return fail(CONNECTION_FAILURES.submitPhone);
    }
    if (!response.ok) {
      return fail(PHONE_FAILURE);
    }
    const data = await jsonOrNull(response);
    if (!data?.success) {
      return fail(PHONE_FAILURE);
    }
    setSessionValue("phone", data.phone);
    return ok(undefined);
  },

  requestSmsVerificationCode: async (): Promise<AuthResult<{ phone?: string }>> => {
    const challengeToken = sessionStorage.getItem("challengeToken");
    if (!challengeToken) {
      return fail("Please try logging in again.");
    }
    let response: Response;
    try {
      response = await post("/requestSmsVerificationCode", { challengeToken });
    } catch {
      return fail(CONNECTION_FAILURES.requestCode);
    }
    if (!response.ok) {
      return fail("Error requesting code. Please contact support@getmanatee.com");
    }
    const data = await jsonOrNull(response);
    if (!data?.success) {
      return fail(stringOr(data?.message, GENERIC_FAILURE));
    }
    if (data.challengeToken) {
      sessionStorage.setItem("challengeToken", data.challengeToken);
    }
    if (data.phone) {
      sessionStorage.setItem("phone", data.phone);
    }
    return ok(typeof data.phone === "string" ? { phone: data.phone } : {});
  },

  verifyTOTP: async (totp: string): Promise<AuthResult<VerifiedUser>> => {
    const challengeToken = sessionStorage.getItem("challengeToken");
    if (!challengeToken) {
      return fail("No challenge token found. Please try logging in again.");
    }
    let response: Response;
    let result: unknown;
    try {
      response = await post("/verifyTOTP", { challengeToken, totp, application: "WEB" });
      result = await response.json();
    } catch {
      return fail(CONNECTION_FAILURES.verifyCode);
    }
    if (!response.ok || result === "invalid" || typeof result !== "object" || result === null) {
      return fail(INVALID_CODE);
    }
    const user = result as VerifiedUser & { message?: string };
    if (user.message) {
      return fail(user.message);
    }
    setSessionValue("userId", user.id);
    setSessionValue("role", user.accountType);
    setSessionValue("user", user.firstName);
    sessionStorage.removeItem("challengeToken");
    if (user.token) {
      setAuthTokenCookie(user.token);
    }
    if (user.twoFAtoken) {
      setTwoFactorCookie(user.twoFAtoken);
    }
    return ok(user);
  },

  register: async (data: RegistrationRequest): Promise<AuthResult<unknown>> => {
    let response: Response;
    try {
      response = await post("/register", data);
    } catch {
      return fail(CONNECTION_FAILURES.register);
    }
    const result = await jsonOrNull(response);
    if (!response.ok) {
      return fail(stringOr(result?.message, stringOr(result?.error, "Unable to register. Please try again later.")));
    }
    return ok(result ?? {});
  },

  getEmailRegistrationOptions: async (email: string): Promise<AuthResult<{ careCoordinationEnabled: boolean }>> => {
    try {
      const response = await post("/registration/isCareCoordinationEnabled", { email });
      const result = await response.json();
      return ok({ careCoordinationEnabled: !!result?.careCoordinationEnabled });
    } catch {
      return fail(CONNECTION_FAILURES.registrationOptions);
    }
  },

  forgotPassword: async (data: { email: string }): Promise<AuthResult<{ message?: string }>> => {
    let response: Response;
    try {
      response = await post("/forgotPassword", data);
    } catch {
      return fail(CONNECTION_FAILURES.forgotPassword);
    }
    const result = await jsonOrNull(response);
    if (!response.ok) {
      return fail(stringOr(result?.message, GENERIC_FAILURE));
    }
    return ok(typeof result?.message === "string" ? { message: result.message } : {});
  },

  passwordReset: async (data: { code: string; password: string }): Promise<AuthResult<void>> => {
    let response: Response;
    try {
      response = await post("/parentPasswordReset", { data });
    } catch {
      return fail(CONNECTION_FAILURES.passwordReset);
    }
    if (!response.ok) {
      const result = await jsonOrNull(response);
      return fail(stringOr(result?.message, GENERIC_FAILURE));
    }
    return ok(undefined);
  },

  logout,
  setAuthToken(token: string, options?: TokenOptions): void {
    setAuthTokenCookie(token, options);
  },
  removeAuthToken,

  consumeOneTimeToken: async (token: string) => {
    try {
      const response = await post("/one-time-auth-tokens/consume", { token });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to authenticate");
      }
      return await response.json();
    } catch (e) {
      throw Error((e as Error).message || "Error consuming one-time token");
    }
  },

  unsubscribeFromEmails: async (email: string) => {
    try {
      const response = await post("/unsubscribe", { contactAddress: email, deliveryType: "EMAIL" });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to unsubscribe");
      }
      return await response.json();
    } catch (e) {
      throw Error((e as Error).message || "Error unsubscribing from emails");
    }
  },

  requestNewOneTimeAuthLink: async (token: string, sendEmail = true) => {
    try {
      const response = await post("/one-time-auth-links", { expiredToken: token, sendEmail });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to request new link");
      }
      return await response.json();
    } catch (e) {
      throw Error((e as Error).message || "Error requesting new one-time auth link");
    }
  }
};

type TokenOptions = { type?: string | undefined; expiresInMillis?: number | undefined };

function logout(): void {
  const authToken = cookies.get(AUTH_COOKIE);
  sessionStorage.removeItem("token");
  sessionStorage.removeItem("role");
  fireEvent("logoutBegin");
  client.clearStore();
  if (authToken) {
    fetch(`${config.env.REACT_APP_API}/logout`, {
      method: "POST",
      headers: { ...JSON_HEADERS, "x-access-token": `${authToken}` }
    }).catch(error => {
      if (IS_DEV) {
        console.error("Error logging out", error);
      }
    });
  }
  removeAuthToken();
}

function removeAuthToken(): void {
  fireEvent("authTokenRemoval");
  cookies.remove(AUTH_COOKIE, { path: "/" });
}

function setSessionValue(key: string, value: unknown): void {
  if (value === undefined || value === null) {
    sessionStorage.removeItem(key);
  } else {
    sessionStorage.setItem(key, String(value));
  }
}

function secureCookies(): boolean {
  return config.env.REACT_APP_ENV === "production" || config.env.REACT_APP_ENV === "staging";
}

export function setAuthTokenCookie(authToken: string, options?: TokenOptions): void {
  if (authToken) {
    fireEvent("authTokenSet", { type: options?.type });
  }
  cookies.set(AUTH_COOKIE, authToken, {
    path: "/",
    secure: secureCookies(),
    sameSite: true,
    expires: new Date(Date.now() + (options?.expiresInMillis || COOKIE_LIFETIME_MS))
  });
}

function setTwoFactorCookie(twoFAToken: string): void {
  cookies.remove(TWO_FACTOR_COOKIE);
  cookies.set(TWO_FACTOR_COOKIE, twoFAToken, {
    path: "/",
    secure: secureCookies(),
    sameSite: true,
    expires: new Date(Date.now() + COOKIE_LIFETIME_MS)
  });
}

export default AuthService;
